/**
 * studio-app-descriptors — the 9 generated clusters the studio composes, as
 * SCHEMA-FREE route descriptors + their compose options (#705 S2).
 *
 * Each entry bundles everything the entry-side {@link studioAppRoutePlugin} needs
 * to project a cluster's route table WITHOUT importing the model's schema:
 *
 *   - a hand-authored {@link RouteDescriptor} literal (the cluster's `id`/`name`/
 *     `version` + its nodes' `{ name, plural }`) — schema-free by construction;
 *   - the route-placement options + transport discriminant (matching the current
 *     `studioAppPlugin(model, …)` / `slicerPluginFamily` compose in `genome-full.ts`
 *     + `slicer/index.ts`);
 *   - a LAZY `loadModel` thunk — a dynamic `import()` of the model, so the schema
 *     loads only inside the deferred editor chunk (off the entry).
 *
 * The descriptor literals are hand-authored (they must NOT import a model, or the
 * schema returns to the entry) and kept honest by the #705 no-drift gate
 * (`studio-app-route-plugin.test.ts` asserts each equals `deriveRouteDescriptor(
 * model)`). This module is ADDITIVE — the #705 cutover (S3) is what composes these
 * into `genome.ts` in place of the eager `studioAppPlugin(model)` calls.
 *
 * @module @ubiclouds/v5-studio/genome/studio-app-descriptors
 */

import type { RouteDescriptor } from '@ubiclouds/v5-genome-gen';
import type { StudioAppRoutePluginOptions } from './studio-app-route-plugin.js';

/** A studio-composed generated cluster: its schema-free route descriptor + compose options. */
export interface StudioClusterDescriptor {
  /** The schema-free route identity (hand-authored; no-drift-gated against the model). */
  readonly descriptor: RouteDescriptor;
  /** The route-placement + transport + lazy-model options `studioAppRoutePlugin` composes with. */
  readonly options: StudioAppRoutePluginOptions;
}

/** printer — the single-node printer CONNECTOR (`omitIndex`, so `/printer` IS the list). */
export const printerCluster: StudioClusterDescriptor = {
  descriptor: {
    id: 'printer',
    name: 'Printers',
    version: '1.0.0',
    nodes: [{ name: 'printer-connection', plural: 'printer' }],
  },
  options: {
    omitIndex: true,
    loadModel: () => import('@ubiclouds/v5-genome-gen/sample').then((m) => m.printerModel),
  },
};

/** todo-app — the §10 todo demonstrator, re-homed off `/` to `/app`. */
export const todoAppCluster: StudioClusterDescriptor = {
  descriptor: {
    id: 'todo-app',
    name: 'Todo App',
    version: '1.0.0',
    nodes: [
      { name: 'todo-list', plural: 'todo-lists' },
      { name: 'todo', plural: 'todos' },
      { name: 'tag', plural: 'tags' },
    ],
  },
  options: {
    loadModel: () => import('@ubiclouds/v5-genome-gen/sample').then((m) => m.todoAppModel),
  },
};

/** host-app — the IDE's Host 1:N Project CRUD core, prefixed under `/host-app`, live-data seam. */
export const hostAppCluster: StudioClusterDescriptor = {
  descriptor: {
    id: 'host-app',
    name: 'Hosts & Projects',
    version: '1.0.0',
    nodes: [
      { name: 'host', plural: 'hosts' },
      { name: 'project', plural: 'projects' },
    ],
  },
  options: {
    routePrefix: '/host-app',
    transport: 'host-app-live',
    loadModel: () => import('@ubiclouds/v5-genome-gen/sample').then((m) => m.hostAppModel),
  },
};

/** agent-profiles — the single-node Gemma AgentProfile CRUD cluster (`omitIndex`). */
export const agentProfilesCluster: StudioClusterDescriptor = {
  descriptor: {
    id: 'agent-profiles',
    name: 'Agent Profiles',
    version: '1.0.0',
    nodes: [{ name: 'agent-profile', plural: 'agent-profiles' }],
  },
  options: {
    omitIndex: true,
    transport: 'agent-profiles-local',
    loadModel: () => import('@ubiclouds/v5-genome-gen/sample').then((m) => m.agentProfilesModel),
  },
};

/** monzo — the account/transaction/pot/merchant banking cluster, prefixed under `/monzo`. */
export const monzoCluster: StudioClusterDescriptor = {
  descriptor: {
    id: 'monzo',
    name: 'Monzo',
    version: '1.0.0',
    nodes: [
      { name: 'account', plural: 'accounts' },
      { name: 'transaction', plural: 'transactions' },
      { name: 'pot', plural: 'pots' },
      { name: 'merchant', plural: 'merchants' },
    ],
  },
  options: {
    routePrefix: '/monzo',
    transport: 'monzo-fixture',
    loadModel: () => import('@ubiclouds/v5-genome-gen/sample').then((m) => m.monzoModel),
  },
};

/** home-battery — the 8-node parts-catalog + configuration cluster, prefixed under `/home-battery`. */
export const homeBatteryCluster: StudioClusterDescriptor = {
  descriptor: {
    id: 'home-battery',
    name: 'Home Battery',
    version: '1.0.0',
    nodes: [
      { name: 'property', plural: 'properties' },
      { name: 'battery-module', plural: 'battery-modules' },
      { name: 'inverter', plural: 'inverters' },
      { name: 'ev-charger', plural: 'ev-chargers' },
      { name: 'electric-vehicle', plural: 'electric-vehicles' },
      { name: 'tariff', plural: 'tariffs' },
      { name: 'system-configuration', plural: 'system-configurations' },
      { name: 'setup-template', plural: 'setup-templates' },
    ],
  },
  options: {
    routePrefix: '/home-battery',
    transport: 'home-battery-local',
    loadModel: () => import('@ubiclouds/v5-genome-gen/sample').then((m) => m.homeBatteryModel),
  },
};

/** slicer-profile — the generated slice-settings CRUD cluster, prefixed under `/slicer-settings`. */
export const slicerProfileCluster: StudioClusterDescriptor = {
  descriptor: {
    id: 'slicer-profile',
    name: 'Slicer Profiles',
    version: '1.0.0',
    nodes: [{ name: 'slicer-profile', plural: 'slicer-profiles' }],
  },
  options: {
    routePrefix: '/slicer-settings',
    transport: 'slicer-profile-local',
    loadModel: () => import('@ubiclouds/v5-genome-gen/sample').then((m) => m.slicerProfileModel),
  },
};

/** filament — the generated filament CRUD cluster, prefixed under `/slicer-filaments`. */
export const filamentCluster: StudioClusterDescriptor = {
  descriptor: {
    id: 'filament',
    name: 'Filaments',
    version: '1.0.0',
    nodes: [{ name: 'filament', plural: 'filaments' }],
  },
  options: {
    routePrefix: '/slicer-filaments',
    transport: 'filament-local',
    loadModel: () => import('@ubiclouds/v5-genome-gen/sample').then((m) => m.filamentModel),
  },
};

/**
 * budget — the 3-node monthly-baseline CRUD core, prefixed under `/budget` (#1392 S1).
 *
 * NO `transport` discriminant: the cluster deliberately takes the DEFAULT `'mock'`
 * wrapper, which is the generic `NodeCollectionStore`-backed harness (#464a).
 * That is the #471 "adopt it from birth" call — a fourth bespoke near-duplicate
 * localStorage transport would have been a fourth thing to retro-fit later.
 */
export const budgetCluster: StudioClusterDescriptor = {
  descriptor: {
    id: 'budget',
    name: 'Budget',
    version: '1.0.0',
    nodes: [
      { name: 'budget-group', plural: 'budget-groups' },
      { name: 'expense-line', plural: 'expense-lines' },
      { name: 'income-line', plural: 'income-lines' },
    ],
  },
  options: {
    routePrefix: '/budget',
    loadModel: () => import('@ubiclouds/v5-genome-gen/sample').then((m) => m.budgetModel),
  },
};

/** All 9 studio-composed generated clusters (the #705 descriptor set). */
export const STUDIO_CLUSTER_DESCRIPTORS: readonly StudioClusterDescriptor[] = [
  printerCluster,
  todoAppCluster,
  hostAppCluster,
  agentProfilesCluster,
  monzoCluster,
  homeBatteryCluster,
  slicerProfileCluster,
  filamentCluster,
  budgetCluster,
];
